import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  FileText, Loader2, CheckCircle2, Send, RefreshCw, Plus, Edit3, RotateCcw, Building2,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { inputCls } from "../../components/forms/FormBits";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const SECTIONS = [
  { k: "professional_summary", label: "Professional Summary", type: "text", rows: 4 },
  { k: "seniority_level", label: "Seniority Level", type: "text", rows: 1 },
  { k: "key_experience", label: "Key Experience", type: "list" },
  { k: "relevant_achievements", label: "Relevant Achievements", type: "list" },
  { k: "core_skills", label: "Core Skills", type: "list" },
  { k: "specialisations", label: "Specialisations", type: "list" },
  { k: "certifications", label: "Certifications", type: "list" },
  { k: "strengths", label: "Strengths", type: "list" },
  { k: "work_rights_compliance", label: "Work Rights & Compliance", type: "list" },
  { k: "potential_gaps_risks", label: "Potential Gaps / Risks", type: "list", risk: true },
  { k: "role_fit", label: "Why This Candidate Fits the Role", type: "text", rows: 4 },
  { k: "recruiter_recommendation", label: "Recruiter Recommendation", type: "text", rows: 2 },
];
const LIST_KEYS = SECTIONS.filter((s) => s.type === "list").map((s) => s.k);

const statusChip = (s) => ({
  Draft: "bg-secondary text-charcoal/60",
  Approved: "bg-gold/15 text-gold-dark",
  Submitted: "bg-success/15 text-success",
}[s] || "bg-secondary text-charcoal/60");

const csv = (v) => (Array.isArray(v) ? v.join("\n") : v || "");
const toList = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);

export default function ClientBriefs() {
  const [briefs, setBriefs] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [genOpen, setGenOpen] = useState(false);
  const [gen, setGen] = useState({ candidate_id: "", role_title: "", client_name: "" });
  const [generating, setGenerating] = useState(false);
  const [active, setActive] = useState(null);
  const [draft, setDraft] = useState(null);
  const [edit, setEdit] = useState(false);
  const [acting, setActing] = useState(false);

  const load = useCallback(async (status) => {
    setLoading(true);
    try { const { data } = await api.get(`/admin/briefs?status=${status}`); setBriefs(data); }
    catch { toast.error("Failed to load briefs"); }
    finally { setLoading(false); }
  }, []);

  const loadCandidates = useCallback(async () => {
    try { const { data } = await api.get("/admin/intel/candidates"); setCandidates(data.filter((c) => c.ai_processed)); }
    catch { /* ignore */ }
  }, []);

  useEffect(() => { load(filter); }, [filter, load]);
  useEffect(() => { loadCandidates(); }, [loadCandidates]);

  const generate = async () => {
    if (!gen.candidate_id) { toast.error("Select an approved candidate."); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/briefs/generate", gen);
      toast.success("Draft brief generated — review before approval");
      setGenOpen(false); setGen({ candidate_id: "", role_title: "", client_name: "" });
      load(filter); openBrief(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Generation failed."); }
    finally { setGenerating(false); }
  };

  const openBrief = (b) => { setActive(b); setDraft(JSON.parse(JSON.stringify(b.content || {}))); setEdit(false); };

  const saveEdits = async () => {
    setActing(true);
    try {
      const content = { ...draft };
      LIST_KEYS.forEach((k) => { content[k] = toList(csv(draft[k])); });
      const { data } = await api.patch(`/admin/briefs/${active.id}`, { content, client_name: active.client_name, role_title: active.role_title });
      setActive(data); setDraft(JSON.parse(JSON.stringify(data.content))); setEdit(false);
      toast.success("Brief updated"); load(filter);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed."); }
    finally { setActing(false); }
  };

  const doAction = async (verb) => {
    setActing(true);
    try {
      const { data } = await api.post(`/admin/briefs/${active.id}/${verb}`, {});
      setActive(data);
      toast.success(verb === "approve" ? "Brief approved" : verb === "submit" ? "Submitted to client" : "Returned to draft");
      load(filter);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Action failed."); }
    finally { setActing(false); }
  };

  const brand = active?.brand_snapshot || {};

  return (
    <div data-testid="briefs-panel">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl text-navy">Client Brief Generator</h2>
          <p className="text-sm text-muted-foreground">Generate client-ready profiles from approved candidates. Nothing is sent without your final approval.</p>
        </div>
        <button onClick={() => setGenOpen(true)} className="inline-flex items-center gap-2 rounded-lg bg-gold px-4 py-2 text-sm font-semibold text-white hover:bg-gold-dark" data-testid="brief-generate-open">
          <Plus className="h-4 w-4" /> Generate Brief
        </button>
      </div>

      <div className="mb-4 flex items-center gap-3">
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-lg border border-border bg-white px-4 py-2 text-sm" data-testid="brief-filter">
          {["all", "Draft", "Approved", "Submitted"].map((s) => <option key={s} value={s}>{s === "all" ? "All" : s}</option>)}
        </select>
        <button onClick={() => load(filter)} className="inline-flex items-center gap-2 rounded-lg border border-border bg-white px-3 py-2 text-sm text-charcoal hover:border-navy"><RefreshCw className="h-4 w-4" /> Refresh</button>
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-navy" /></div>
        : briefs.length === 0 ? <div className="rounded-2xl border border-dashed border-border bg-white py-16 text-center text-sm text-muted-foreground">No briefs yet. Generate one from an approved candidate.</div>
        : <div className="space-y-3" data-testid="brief-list">
            {briefs.map((b) => (
              <button key={b.id} onClick={() => openBrief(b)} className="flex w-full flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-white p-5 text-left hover:shadow-sm" data-testid={`brief-${b.id}`}>
                <div>
                  <p className="font-serif text-lg text-navy">{b.candidate_name}</p>
                  <p className="text-sm text-muted-foreground">{b.industry} · {b.role_title || "—"} {b.client_name ? `· ${b.client_name}` : ""}</p>
                </div>
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusChip(b.status)}`}>{b.status}</span>
              </button>
            ))}
          </div>}

      {/* Generate modal */}
      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-serif text-navy">Generate Client Brief</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-xs font-semibold text-charcoal/70">Approved candidate</label>
              <select className={inputCls} value={gen.candidate_id} onChange={(e) => setGen((g) => ({ ...g, candidate_id: e.target.value }))} data-testid="brief-candidate-select">
                <option value="">Select a screened & approved candidate…</option>
                {candidates.map((c) => <option key={c.id} value={c.id}>{c.full_name} — {c.profession} {c.medical_specialty ? `(${c.medical_specialty})` : ""}</option>)}
              </select>
              {candidates.length === 0 && <p className="mt-1 text-xs text-muted-foreground">No approved candidates yet. Approve a screening review first.</p>}
            </div>
            <div><label className="mb-1 block text-xs font-semibold text-charcoal/70">Role title (optional)</label><input className={inputCls} value={gen.role_title} onChange={(e) => setGen((g) => ({ ...g, role_title: e.target.value }))} data-testid="brief-role" /></div>
            <div><label className="mb-1 block text-xs font-semibold text-charcoal/70">Client name (optional)</label><input className={inputCls} value={gen.client_name} onChange={(e) => setGen((g) => ({ ...g, client_name: e.target.value }))} data-testid="brief-client" /></div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setGenOpen(false)} className="rounded-lg border border-border px-5 py-2.5 text-sm font-semibold text-charcoal">Cancel</button>
              <button onClick={generate} disabled={generating} className="inline-flex items-center gap-2 rounded-lg bg-navy px-6 py-2.5 text-sm font-semibold text-white hover:bg-navy-light disabled:opacity-70" data-testid="brief-generate-submit">
                {generating && <Loader2 className="h-4 w-4 animate-spin" />} Generate Draft
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Brief detail / branded preview */}
      <Dialog open={!!active} onOpenChange={(o) => { if (!o) { setActive(null); setDraft(null); setEdit(false); } }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader><DialogTitle className="font-serif text-navy">Candidate Brief · {active?.status}</DialogTitle></DialogHeader>
          {active && draft && (
            <div className="space-y-5">
              {/* Branded header (dynamic from brand snapshot) */}
              <div className="flex items-center gap-4 rounded-xl p-4 text-white" style={{ background: brand.primary_color || "#0F2942" }} data-testid="brief-brand-header">
                {brand.logo_url && <img src={brand.logo_url} alt={brand.company_name} className="h-12 w-12 rounded-full object-cover ring-1 ring-white/20" />}
                <div>
                  <p className="font-serif text-lg">{brand.company_name}</p>
                  <p className="text-xs" style={{ color: brand.accent_color || "#C2751A" }}>{brand.tagline}</p>
                </div>
                <span className="ml-auto rounded-full bg-white/10 px-3 py-1 text-xs">Confidential Candidate Brief</span>
              </div>

              <div>
                <p className="font-serif text-xl text-navy">{active.candidate_name}</p>
                {edit ? (
                  <div className="mt-2 grid gap-2 sm:grid-cols-3">
                    <input className={inputCls} placeholder="Headline" value={draft.headline || ""} onChange={(e) => setDraft((d) => ({ ...d, headline: e.target.value }))} data-testid="brief-edit-headline" />
                    <input className={inputCls} placeholder="Role title" value={active.role_title || ""} onChange={(e) => setActive((a) => ({ ...a, role_title: e.target.value }))} data-testid="brief-edit-role_title" />
                    <input className={inputCls} placeholder="Client name" value={active.client_name || ""} onChange={(e) => setActive((a) => ({ ...a, client_name: e.target.value }))} data-testid="brief-edit-client_name" />
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground">{draft.headline || active.industry}</p>
                    {active.role_title && <p className="mt-1 text-sm text-charcoal">Proposed for: <strong>{active.role_title}</strong>{active.client_name ? ` · ${active.client_name}` : ""}</p>}
                  </>
                )}
                {!active.ai_available && <p className="mt-2 rounded bg-gold/10 px-3 py-1.5 text-xs text-charcoal/70">AI was offline for this draft — assembled from approved data. Edit before approving.</p>}
              </div>

              {edit && (
                <div className="rounded-lg border border-navy/15 bg-navy/[0.03] px-4 py-2.5 text-xs text-navy" data-testid="brief-edit-workflow-note">
                  <b>Editing:</b> refine any sentence or section below. Workflow — AI Draft → CEO Edit → Review → <b>Approve</b> → Final Submission. Editing an approved brief resets it to Draft for re-approval.
                </div>
              )}

              {SECTIONS.map(({ k, label, type, rows, risk }) => (
                <div key={k}>
                  <h4 className={`text-xs font-semibold uppercase tracking-wider ${risk ? "text-destructive" : "text-gold-dark"}`}>{label}</h4>
                  {edit ? (
                    <textarea rows={type === "list" ? 3 : (rows || 2)} className={`${inputCls} mt-1`} value={type === "list" ? csv(draft[k]) : (draft[k] || "")} onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))} placeholder={type === "list" ? "One item per line" : ""} data-testid={`brief-edit-${k}`} />
                  ) : type === "list" ? (
                    <ul className="mt-1 list-inside list-disc text-sm text-charcoal">{(draft[k] || []).length ? draft[k].map((x, i) => <li key={i}>{x}</li>) : <span className="text-muted-foreground">—</span>}</ul>
                  ) : (
                    <p className="mt-1 text-sm leading-relaxed text-charcoal">{draft[k] || "—"}</p>
                  )}
                </div>
              ))}

              <p className="border-t border-border pt-3 text-[11px] text-muted-foreground">{brand.footer_note}</p>

              {/* Actions */}
              <div className="sticky bottom-0 flex flex-wrap justify-end gap-3 border-t border-border bg-white pt-4">
                {active.status !== "Submitted" && (
                  edit ? (
                    <button onClick={saveEdits} disabled={acting} className="inline-flex items-center gap-2 rounded-lg border border-navy/20 px-5 py-2.5 text-sm font-semibold text-navy hover:border-navy disabled:opacity-60" data-testid="brief-save">
                      {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Edit3 className="h-4 w-4" />} Save Edits
                    </button>
                  ) : (
                    <button onClick={() => setEdit(true)} className="inline-flex items-center gap-2 rounded-lg border border-border px-5 py-2.5 text-sm font-semibold text-charcoal hover:border-navy" data-testid="brief-edit-toggle">
                      <Edit3 className="h-4 w-4" /> Edit
                    </button>
                  )
                )}
                {active.status === "Draft" && !edit && (
                  <button onClick={() => doAction("approve")} disabled={acting} className="inline-flex items-center gap-2 rounded-lg bg-gold px-6 py-2.5 text-sm font-semibold text-white hover:bg-gold-dark disabled:opacity-60" data-testid="brief-approve">
                    {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Approve (final review)
                  </button>
                )}
                {active.status === "Approved" && (
                  <>
                    <button onClick={() => doAction("reject")} disabled={acting} className="inline-flex items-center gap-2 rounded-lg border border-border px-5 py-2.5 text-sm font-semibold text-charcoal hover:border-navy disabled:opacity-60" data-testid="brief-return"><RotateCcw className="h-4 w-4" /> Return to Draft</button>
                    <button onClick={() => doAction("submit")} disabled={acting} className="inline-flex items-center gap-2 rounded-lg bg-success px-6 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60" data-testid="brief-submit">
                      {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Submit to Client
                    </button>
                  </>
                )}
                {active.status === "Submitted" && <span className="inline-flex items-center gap-2 rounded-lg bg-success/10 px-4 py-2 text-sm font-semibold text-success"><CheckCircle2 className="h-4 w-4" /> Submitted to client</span>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
